import React from 'react';
import { Link } from 'react-router-dom';
import { Car, MessageCircle, Clock, Zap, ArrowRight, Bell } from 'lucide-react';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';
import { FeatureCard } from '../components/ui/FeatureCard';

export const CarHunterAiPage: React.FC = () => {
    usePageMetadata({
        title: "CarHunter AI | Alerty o Okazjach z Mobile.de na WhatsApp",
        description: "Bądź pierwszy przy najlepszych okazjach z mobile.de! CarHunter AI monitoruje ogłoszenia 24/7 i wysyła alerty o autach na WhatsApp w 2 minuty.",
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "CarHunter AI",
            "serviceType": "Monitoring Ofert Motoryzacyjnych",
            "provider": {
                "@type": "Organization",
                "name": "BiznesBezKlikania.pl"
            },
            "description": "Inteligentny system do wyszukiwania okazji motoryzacyjnych. Monitoruje portale ogłoszeniowe 24/7 i wysyła natychmiastowe alerty o najlepszych ofertach.",
            "url": "https://biznesbezklikania.pl/rozwiazania/carhunter-ai"
        }
    });

    const features = [
        { icon: <Clock className="w-8 h-8" />, title: 'Monitoring 24/7', description: 'System nieustannie skanuje mobile.de w poszukiwaniu nowych ogłoszeń pasujących do Twoich kryteriów.' },
        { icon: <Zap className="w-8 h-8" />, title: 'Alert w 2 minuty', description: 'Otrzymujesz powiadomienie na WhatsApp w mniej niż 2 minuty od publikacji ogłoszenia.' },
        { icon: <Bell className="w-8 h-8" />, title: 'Spersonalizowane filtry', description: 'Ustaw dokładne parametry: marka, model, rocznik, przebieg, cena - i otrzymuj tylko trafne oferty.' },
        { icon: <MessageCircle className="w-8 h-8" />, title: 'Wygodny WhatsApp', description: 'Alerty przychodzą tam, gdzie i tak jesteś - na WhatsApp. Bez instalowania dodatkowych aplikacji.' },
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[70vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="blue" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <RevealOnScroll>
                                <p className="text-sm font-semibold text-emerald-500 dark:text-emerald-400 uppercase tracking-wider mb-4">
                                    Monitoring mobile.de
                                </p>
                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight">
                                    <span className="bg-gradient-to-r from-emerald-500 via-primary-light to-emerald-500 dark:from-emerald-400 dark:via-primary-dark dark:to-emerald-400 bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                                        Najszybsze alerty
                                    </span>
                                    <br />
                                    <span className="text-text-primary-light dark:text-text-primary-dark">
                                        o autach z Niemiec
                                    </span>
                                </h1>
                            </RevealOnScroll>

                            <RevealOnScroll delay={100}>
                                <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark mb-8">
                                    Bądź pierwszy przy najlepszych okazjach z <strong>mobile.de</strong> - w mniej niż 2 minuty od publikacji. Alerty prosto na WhatsApp.
                                </p>
                            </RevealOnScroll>

                            <RevealOnScroll delay={200}>
                                <a
                                    href="https://wa.me/48791581239?text=START&ref=BBK-carhunter-ai"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-8 py-4 font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 dark:from-emerald-400 dark:to-emerald-500 rounded-xl hover:shadow-xl hover:shadow-emerald-500/30 dark:hover:shadow-emerald-400/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group"
                                >
                                    <MessageCircle className="w-5 h-5 mr-2" />
                                    Rozpocznij 7 dni za darmo
                                    <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                                </a>
                            </RevealOnScroll>
                        </div>

                        {/* WhatsApp Phone Mockup */}
                        <RevealOnScroll delay={300} direction="right">
                            <GlassCard variant="strong" className="p-0 overflow-hidden max-w-sm mx-auto">
                                {/* Phone frame */}
                                <div className="bg-gray-900 p-2 rounded-t-2xl">
                                    <div className="flex justify-center">
                                        <div className="w-20 h-1 bg-gray-700 rounded-full" />
                                    </div>
                                </div>

                                {/* WhatsApp Header */}
                                <div className="bg-emerald-700 text-white p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center">
                                            <Car className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <p className="font-semibold">CarHunter Bot</p>
                                            <p className="text-xs text-emerald-200 flex items-center gap-1">
                                                <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
                                                online
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Chat Messages */}
                                <div className="bg-[#E5DDD5] dark:bg-gray-800 p-4 space-y-3 h-80 overflow-y-auto">
                                    <div className="bg-white dark:bg-gray-700 p-3 rounded-lg rounded-tl-none max-w-[85%] shadow-sm">
                                        <p className="text-gray-800 dark:text-gray-200 text-sm">
                                            <span role="img" aria-label="car">🚗</span> <strong>BMW 320d Touring</strong>
                                        </p>
                                        <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
                                            2019 • 89.000 km • Automat
                                        </p>
                                        <p className="text-emerald-600 dark:text-emerald-400 font-bold mt-2">
                                            💶 18.500 EUR
                                        </p>
                                        <p className="text-blue-600 dark:text-blue-400 text-xs mt-2 underline">
                                            mobile.de/offer/123456
                                        </p>
                                        <p className="text-gray-400 text-xs mt-2 text-right">14:32 ✓✓</p>
                                    </div>

                                    <div className="bg-white dark:bg-gray-700 p-3 rounded-lg rounded-tl-none max-w-[85%] shadow-sm">
                                        <p className="text-gray-800 dark:text-gray-200 text-sm">
                                            <span role="img" aria-label="fire">🔥</span> <strong>Audi A4 Avant</strong>
                                        </p>
                                        <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
                                            2020 • 62.000 km • S-line
                                        </p>
                                        <p className="text-emerald-600 dark:text-emerald-400 font-bold mt-2">
                                            💶 24.900 EUR
                                        </p>
                                        <p className="text-gray-400 text-xs mt-2 text-right">14:35 ✓✓</p>
                                    </div>
                                </div>

                                {/* Phone bottom */}
                                <div className="bg-gray-900 p-3 rounded-b-2xl">
                                    <div className="flex justify-center">
                                        <div className="w-24 h-1 bg-gray-700 rounded-full" />
                                    </div>
                                </div>
                            </GlassCard>
                        </RevealOnScroll>
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* Features Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Dlaczego CarHunter AI?
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                            Zawsze bądź o krok przed konkurencją. Złap najlepsze okazje, zanim zrobią to inni.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => (
                            <RevealOnScroll key={feature.title} delay={index * 100}>
                                <FeatureCard
                                    icon={feature.icon}
                                    title={feature.title}
                                    description={feature.description}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {/* Case Study CTA */}
            <section className="py-16 md:py-20 relative bg-content-light/50 dark:bg-content-dark/50">
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll>
                        <GlassCard variant="strong" className="text-center max-w-3xl mx-auto">
                            <h2 className="text-2xl md:text-3xl font-bold text-text-primary-light dark:text-text-primary-dark mb-4">
                                Zobacz realne wyniki
                            </h2>
                            <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mb-8">
                                Sprawdź, jak Auto-Handel "Okazja" zyskał przewagę nad konkurencją, osiągając 1200% ROI w pierwszym kwartale dzięki alertom z CarHunter AI.
                            </p>
                            <Link
                                to="/case-studies#auto-handel"
                                className="px-6 py-3 font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 dark:from-emerald-400 dark:to-emerald-500 rounded-xl hover:shadow-xl hover:shadow-emerald-500/30 dark:hover:shadow-emerald-400/30 hover:scale-[1.02] transition-all duration-300 inline-flex items-center group"
                            >
                                Przeczytaj Case Study
                                <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            <WhatNextSection diyLink="/rozwiazania/carhunter-ai" />
        </>
    );
};
